// Legendas do vídeo de App Review da Meta (permissão business_management),
// exibidas e narradas pelo modo de gravação (?reviewMode=1 ou ?reviewMode=manual).
// São as mesmas frases do roteiro, dos .srt e dos textos de narração em
// docs/meta-app-review/ — ao mudar uma, rode de novo o gerador descrito no
// ROTEIRO_GRAVACAO.md. Ficam em inglês de propósito: a Meta exige o vídeo em inglês.
import type { ReviewEvent } from '../utils/reviewEvents';

export type ReviewCaptionSet = 'auto' | 'manual';

export interface ReviewCaption {
  text: string;
  // Pula para esta legenda quando o fluxo da Meta dispara o evento.
  trigger?: ReviewEvent;
  // Depois de narrar esta legenda, espera este tempo e avança sozinho.
  autoNextMs?: number;
}

const OPENING: ReviewCaption[] = [
  { text: 'SinTicket is a customer service platform where a team of agents answers WhatsApp messages. This video shows how SinTicket uses the business_management permission.' },
  { text: 'An administrator signs in to SinTicket. The interface language is set to English.' },
  { text: 'Settings → Integrations → Channels lists the WhatsApp numbers connected to SinTicket.' },
];

// Últimos passos, iguais nos dois fluxos. O passo que salva o canal muda de
// botão (Connect no automático, Create Channel no manual), então fica em cada set.
const CLOSING: ReviewCaption[] = [
  { text: 'The new channel appears in the list. From its card, the user manages message templates and the WhatsApp business profile.' },
  { text: 'A customer message arrives in the SinTicket inbox, and the agent replies from SinTicket.' },
];

export const REVIEW_CAPTION_SETS: Record<ReviewCaptionSet, ReviewCaption[]> = {
  // Fluxo Automático: Embedded Signup (login da Meta dentro do SinTicket).
  auto: [
    ...OPENING,
    { text: '"New connection" adds a number. We name the channel, choose "Meta Official API" and the Automatic mode. Hovering over each control shows what it does.' },
    { text: '"Continue with Facebook" opens the Meta login window (Facebook Login for Business with WhatsApp Embedded Signup).' },
    { text: 'The user signs in to Facebook with their own account.', trigger: 'meta-popup-opened', autoNextMs: 15000 },
    { text: 'The user chooses their business portfolio and WhatsApp Business Account, and grants SinTicket the requested permissions, including business_management.' },
    { text: 'Back in SinTicket, we list the business portfolios the user granted access to (read with business_management) and the WhatsApp Business Accounts of each portfolio.', trigger: 'meta-code-received' },
    { text: 'The user confirms the business portfolio, the WhatsApp Business Account and the phone number, and clicks "Connect".' },
    { text: '"Connect" creates the channel. SinTicket subscribes the WhatsApp Business Account to its webhook to receive messages.' },
    ...CLOSING,
    { text: "After this login, SinTicket's backend calls the Meta Graph API server-to-server with the token the user granted. No other Meta login is shown in the front end." },
  ],
  // Fluxo Manual (plano B): token de System User gerado no Meta Business Suite.
  manual: [
    ...OPENING,
    { text: '"New connection" adds a number. We name the channel, choose "Meta Official API" and the Manual mode. Hovering over each control shows what it does.' },
    { text: 'In Meta Business Suite, the business admin signs in and opens Business settings → System users.', autoNextMs: 20000 },
    { text: 'The admin generates an access token for the SinTicket app and selects the permissions granted to it, including business_management.' },
    { text: 'Back in SinTicket, the admin pastes the token. "Discover automatically" lists what the token can access.' },
    { text: 'SinTicket reads the business portfolios granted to the token (with business_management) and their WhatsApp Business Accounts and phone numbers.' },
    { text: 'The admin confirms the business portfolio, the WhatsApp Business Account and the phone number, and clicks "Use these values".' },
    { text: '"Create Channel" saves the connection. SinTicket subscribes the WhatsApp Business Account to its webhook to receive messages.' },
    ...CLOSING,
    { text: "SinTicket's backend calls the Meta Graph API server-to-server with this system user token. In this flow there is no Meta login in SinTicket's front end." },
  ],
};
